/**
 * Translation Cache Service
 *
 * Implements caching strategies for translation files to reduce network requests
 * and improve Time to Interactive on repeat visits.
 *
 * Uses IndexedDB for persistent storage with fallback to localStorage
 */

import logger from './logger';
import { logLevels, logEvents } from '../constants/appConstants';

const DB_NAME = 'spotlight-translations';
const DB_VERSION = 1;
const STORE_NAME = 'translations';
const CACHE_EXPIRATION_MS = 24 * 60 * 60 * 1000; // 24 hours

let db = null;

/**
 * Initialize IndexedDB for translation caching
 */
export const initTranslationCache = async() => {
    try {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => {
                logger(logLevels.WARN, {
                    source: 'translationCache',
                    action: logEvents.EXTERNAL_EVENT_CALLED,
                    payload: 'IndexedDB open failed',
                });
                reject(request.error);
            };

            request.onsuccess = () => {
                db = request.result;
                logger(logLevels.DEBUG, {
                    source: 'translationCache',
                    action: logEvents.COMPONENT_MOUNTED,
                    payload: 'IndexedDB initialized for translations',
                });
                resolve(db);
            };

            request.onupgradeneeded = (event) => {
                const database = event.target.result;
                if (!database.objectStoreNames.contains(STORE_NAME)) {
                    const store = database.createObjectStore(STORE_NAME, {
                        keyPath: 'url',
                    });
                    store.createIndex('timestamp', 'timestamp', { unique: false });
                    logger(logLevels.DEBUG, {
                        source: 'translationCache',
                        action: logEvents.COMPONENT_MOUNTED,
                        payload: 'IndexedDB object store created',
                    });
                }
            };
        });
    } catch (error) {
        logger(logLevels.WARN, {
            source: 'translationCache',
            action: logEvents.UNHANDLED_ERROR,
            payload: {
                message: 'Failed to initialize IndexedDB',
                error: error.message,
            },
        });
        throw error;
    }
};

/**
 * Get cached translation from IndexedDB
 */
export const getCachedTranslation = async(url) => {
    try {
        // Make sure DB is initialized
        if (!db) {
            try {
                await initTranslationCache();
            } catch {
                return null; // IndexedDB not available, return null
            }
        }

        return await new Promise((resolve) => {
            try {
                const transaction = db.transaction(STORE_NAME, 'readonly');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.get(url);

                request.onsuccess = () => {
                    const cached = request.result;

                    if (cached) {
                        const now = Date.now();
                        const age = now - cached.timestamp;

                        // Check if cache is still valid
                        if (age < CACHE_EXPIRATION_MS) {
                            logger(logLevels.DEBUG, {
                                source: 'translationCache',
                                action: logEvents.CACHE_HIT,
                                payload: { url, age: Math.round(age / 1000) + 's' },
                            });
                            resolve(cached.data);
                        } else {
                            // Cache expired, delete it
                            const deleteTransaction = db.transaction(STORE_NAME, 'readwrite');
                            deleteTransaction.objectStore(STORE_NAME).delete(url);
                            logger(logLevels.DEBUG, {
                                source: 'translationCache',
                                action: logEvents.CACHE_MISS,
                                payload: { url, reason: 'expired' },
                            });
                            resolve(null);
                        }
                    } else {
                        logger(logLevels.DEBUG, {
                            source: 'translationCache',
                            action: logEvents.CACHE_MISS,
                            payload: { url, reason: 'not found' },
                        });
                        resolve(null);
                    }
                };

                request.onerror = () => {
                    logger(logLevels.WARN, {
                        source: 'translationCache',
                        action: logEvents.UNHANDLED_ERROR,
                        payload: 'Cache read failed',
                    });
                    resolve(null);
                };
            } catch (error) {
                logger(logLevels.WARN, {
                    source: 'translationCache',
                    action: logEvents.UNHANDLED_ERROR,
                    payload: error.message,
                });
                resolve(null);
            }
        });
    } catch (error) {
        logger(logLevels.WARN, {
            source: 'translationCache',
            action: logEvents.UNHANDLED_ERROR,
            payload: error.message,
        });
        return null;
    }
};

/**
 * Cache translation in IndexedDB
 */
/**
 * Cache a translation object
 */
export const cacheTranslation = async(url, data) => {
    try {
        if (!db) {
            try {
                await initTranslationCache();
            } catch (initError) {
                return undefined;
            }
        }

        await new Promise((resolve) => {
            try {
                const transaction = db.transaction(STORE_NAME, 'readwrite');
                const store = transaction.objectStore(STORE_NAME);

                const cacheEntry = {
                    url,
                    data,
                    timestamp: Date.now(),
                };

                const request = store.put(cacheEntry);

                request.onsuccess = () => {
                    logger(logLevels.DEBUG, {
                        source: 'translationCache',
                        action: logEvents.CACHE_WRITE,
                        payload: {
                            url,
                            dataSize: JSON.stringify(data).length,
                        },
                    });
                    resolve();
                };

                request.onerror = () => {
                    logger(logLevels.WARN, {
                        source: 'translationCache',
                        action: logEvents.UNHANDLED_ERROR,
                        payload: 'Cache write failed',
                    });
                    resolve();
                };
            } catch (error) {
                resolve();
            }
        });
        return undefined;
    } catch (error) {
        logger(logLevels.WARN, {
            source: 'translationCache',
            action: logEvents.UNHANDLED_ERROR,
            payload: error.message,
        });
        return undefined;
    }
};

/**
 * Clear all cached translations
 */
export const clearTranslationCache = async() => {
    try {
        if (!db) {
            return undefined;
        }

        await new Promise((resolve) => {
            try {
                const transaction = db.transaction(STORE_NAME, 'readwrite');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.clear();

                request.onsuccess = () => {
                    logger(logLevels.DEBUG, {
                        source: 'translationCache',
                        action: logEvents.CACHE_CLEAR,
                        payload: 'Translation cache cleared',
                    });
                    resolve();
                };

                request.onerror = () => {
                    logger(logLevels.WARN, {
                        source: 'translationCache',
                        action: logEvents.UNHANDLED_ERROR,
                        payload: 'Cache clear failed',
                    });
                    resolve();
                };
            } catch (error) {
                resolve();
            }
        });
        return undefined;
    } catch (error) {
        logger(logLevels.WARN, {
            source: 'translationCache',
            action: logEvents.UNHANDLED_ERROR,
            payload: error.message,
        });
        return undefined;
    }
};

/**
 * Prefetch translation file to cache
 * Called proactively to warm up the cache
 */
export const prefetchTranslation = async(url) => {
    try {
        // Check if already cached
        const cached = await getCachedTranslation(url);
        if (cached) {
            logger(logLevels.DEBUG, {
                source: 'translationCache',
                action: logEvents.CACHE_HIT,
                payload: { action: 'prefetch', url },
            });
            return cached;
        }

        // Fetch from network
        const response = await fetch(url, {
            method: 'GET',
            cache: 'default',
            credentials: 'same-origin',
        });

        if (!response.ok) {
            logger(logLevels.WARN, {
                source: 'translationCache',
                action: logEvents.UNHANDLED_ERROR,
                payload: { action: 'prefetch_failed', status: response.status },
            });
            throw new Error(`Prefetch failed with status ${response.status}`);
        }

        const data = await response.json();
        // Cache the result asynchronously (don't await, fire and forget)
        cacheTranslation(url, data).catch((error) => {
            logger(logLevels.DEBUG, {
                source: 'translationCache',
                action: logEvents.UNHANDLED_ERROR,
                payload: {
                    message: 'Failed to cache translation',
                    error: error.message,
                },
            });
        });
        logger(logLevels.DEBUG, {
            source: 'translationCache',
            action: logEvents.EXTERNAL_EVENT_CALLED,
            payload: { action: 'prefetch_success', url },
        });
        return data;
    } catch (error) {
        logger(logLevels.WARN, {
            source: 'translationCache',
            action: logEvents.UNHANDLED_ERROR,
            payload: { action: 'prefetch_error', error: error.message },
        });
        throw error;
    }
};

export default {
    initTranslationCache,
    getCachedTranslation,
    cacheTranslation,
    clearTranslationCache,
    prefetchTranslation,
};
